<template>
  <div class="agent-chat">
    <!-- 智能体信息栏 -->
    <div class="agent-info-bar" v-if="selectedAgent">
      <div class="agent-avatar">
        <img :src="selectedAgent.agentAvatar || selectedAgent.avatar || defaultAvatar"
          :alt="selectedAgent.agentName || selectedAgent.name" />
      </div>
      <div class="agent-details">
        <h3 class="agent-name">{{ selectedAgent.agentName || selectedAgent.name }}</h3>
        <p class="agent-description">{{ selectedAgent.agentDesc || selectedAgent.description }}</p>
      </div>
      <div class="agent-actions">
        <el-button size="small" @click="clearChat">清空对话</el-button>
      </div>
    </div>
    <div class="chat-container">
      <!-- 聊天轮次列表 -->
      <div class="chat-list" v-if="selectedAgent">
        <div class="session-header">
          <h4>聊天轮次</h4>
          <div class="header-actions">
            <el-button size="mini" type="text" @click="createNewSession" title="新增轮次">
              <i class="el-icon-plus"></i>
            </el-button>
            <el-button size="mini" type="text" @click="loadSessionList" title="刷新">
              <i class="el-icon-refresh"></i>
            </el-button>
          </div>
        </div>
        <div class="session-list" v-loading="sessionLoading">
          <div v-for="session in sessionList" :key="session.sessionGuid" class="session-item"
            :class="{ active: currentSessionGuid === session.sessionGuid }" @click="selectSession(session)">
            <div class="session-info">
              <div class="session-title">{{ session.sessionTitle }}</div>
              <div class="session-meta">
                <span class="session-time">{{ session.lastMessageTime || session.createTime }}</span>
                <span class="session-count" v-if="session.messageCount">{{ session.messageCount }}条消息</span>
              </div>
            </div>
            <div class="session-actions" @click.stop>
              <el-dropdown @command="handleSessionAction" trigger="click">
                <span class="el-dropdown-link">
                  <i class="el-icon-more"></i>
                </span>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item :command="{ action: 'delete', session }">删除轮次</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </div>
        </div>
      </div>
      <div class="chat-content">
        <!-- 未选择智能体的提示 -->
        <div v-if="!selectedAgent" class="no-agent-selected">
          <div class="empty-state">
            <i class="el-icon-chat-dot-round"></i>
            <h3>请选择一个智能体开始对话</h3>
            <p>从智能体广场选择一个智能体，开始您的AI对话之旅</p>
            <el-button type="primary" @click="goToMarketplace">前往智能体广场</el-button>
          </div>
        </div>

        <!-- 对话区域 -->
        <div v-if="selectedAgent" class="chat-area">
          <!-- 对话历史 -->
          <div class="chat-history" ref="chatHistory" v-loading="messagesLoading">
            <!-- 智能体详情和预设问题作为第一条消息 -->
            <div v-if="selectedAgent" class="message-item agent-intro-message">
              <div class="message assistant">
                <div class="avatar">
                  <img
                    :src="agentDetail.agentAvatar || selectedAgent.agentAvatar || selectedAgent.avatar || defaultAvatar"
                    :alt="agentDetail.agentName || selectedAgent.agentName || selectedAgent.name" />
                </div>
                <div class="content">
                  <div class="message-content agent-intro-content">
                    <div class="agent-detail-inline">
                      <h4>{{ agentDetail.agentName || selectedAgent.agentName || selectedAgent.name }}</h4>
                      <p class="agent-desc-inline">{{ agentDetail.agentDesc || selectedAgent.agentDesc ||
                        selectedAgent.description }}</p>

                      <!-- 调试信息 -->
                      <div v-if="$store.state.debug" style="font-size: 12px; color: #999; margin: 10px 0;">
                        <p>agentDetail存在: {{ !!agentDetail }}</p>
                        <p>commonQuestions存在: {{ !!(agentDetail && agentDetail.commonQuestions) }}</p>
                        <p>commonQuestions长度: {{ agentDetail && agentDetail.commonQuestions ?
                          agentDetail.commonQuestions.length : 0 }}</p>
                      </div>

                      <!-- 预设问题 -->
                      <div v-if="agentDetail.commonQuestions && agentDetail.commonQuestions.length > 0"
                        class="common-questions-inline">
                        <div class="questions-title">💡 常见问题，点击快速提问：</div>
                        <div class="question-list-inline">
                          <div v-for="(question, index) in agentDetail.commonQuestions" :key="index"
                            class="question-item-inline" @click="sendPresetQuestion(question)">
                            {{ question }}
                          </div>
                        </div>
                      </div>

                      <!-- 如果没有预设问题，显示提示 -->
                      <div v-else class="no-questions-tip" style="font-size: 13px; color: #999; margin-top: 10px;">
                        暂无预设问题
                      </div>
                    </div>
                  </div>
                  <div class="message-time">{{ formatTime(new Date()) }}</div>
                </div>
              </div>
            </div>

            <!-- 普通对话消息 -->
            <div v-for="(message, index) in chatMessages" :key="index" class="chat-item"
              :class="message.role === 'user' ? 'mine' : ''">
              <!-- 助手头像 -->
              <div class="header-img ml10" v-if="message.role === 'assistant'">
                <img
                  :src="agentDetail.agentAvatar || selectedAgent.agentAvatar || selectedAgent.avatar || defaultAvatar"
                  :alt="agentDetail.agentName || selectedAgent.agentName || selectedAgent.name" />
              </div>

              <!-- 用户头像 -->
              <div class="header-img ml10" v-if="message.role === 'user'">
                <i class="el-icon-user-solid user-avatar-icon"></i>
              </div>

              <!-- 消息内容 -->
              <div class="content">
                <div v-show="message.starting" class="loading">思考中 ...</div>

                <!-- 普通消息内容 -->
                <v-md-preview v-if="!message.starting" class="preview-box" :text="message.content"></v-md-preview>

                <!-- 立即订阅按钮（当type为purchase_needed时显示） -->
                <div v-if="message.type === 'purchase_needed' && !message.starting" class="purchase-buttons">
                  <el-button type="primary" size="small" @click="handlePurchaseOrder" :loading="purchaseLoading">
                    <i class="el-icon-star-on"></i> 立即订阅
                  </el-button>
                </div>

                <!-- 暂停推理按钮（只在非购买需求消息时显示） -->
                <div class="pause-box" v-if="message.role === 'assistant' && message.type !== 'purchase_needed'"
                  @click="onPause" v-show="!message.starting && !message.finished">
                  <i class="el-icon-video-pause"></i>暂停推理
                </div>
              </div>

              <!-- 操作按钮 -->
              <div class="copy">
                <a @click="copyMessage(message.content)">
                  <i class="el-icon-document-copy"></i>
                </a>
                <a class="star" v-if="message.role === 'user'" @click="collectMessage(message)">
                  <i class="el-icon-star-off"></i>
                </a>
              </div>
            </div>
          </div>

          <!-- 输入区域 -->
          <div class="chat-input">
            <div class="input-container">
              <div class="input-row">
                <el-input v-model="inputMessage" type="textarea" :rows="2" placeholder="请输入您的问题..."
                  @keydown.ctrl.enter="sendMessage" :disabled="isLoading" class="message-input" />
                <div>
                  <p class="input-tips">
                    <span class="shortcut-tip">Ctrl + Enter 发送</span>
                  </p>
                  <el-button type="primary" @click="sendMessage" :loading="isLoading" :disabled="!inputMessage.trim()"
                    class="send-button">
                    发送
                  </el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- 购买弹窗 -->
    <pay-meal-dialog ref="paymeal" @onBuyMeal="onBuyMeal"></pay-meal-dialog>
    <pay-vip-dialog ref="vipDialogRef" @onBuyVip="onBuyVip"></pay-vip-dialog>
  </div>
</template>

<script>
import {
  agentDetailApi,
  saveMsgApi,
  createPurchaseOrderApi,
  queryPurchaseOrderApi,
  deleteAllMessagesApi,
  getMessageHistoryApi,
  getSessionListApi,
  createSessionApi,
  switchSessionApi,
  deleteSessionByIdApi
} from '@/apis/agent'
import config from '@/apis/config'
import { getToken } from '@/utils/auth'
import AgentChatWs from '@/utils/agent-chat-ws'
import PayMealDialog from '@/views/app/dialogs/pay-meal-dialog.vue'
import PayVipDialog from '@/views/app/dialogs/pay-vip-dialog.vue'

export default {
  name: 'agent-chat',

  components: {
    PayMealDialog,
    PayVipDialog
  },

  props: {
    selectedAgent: {
      type: Object,
      default: null
    },
    activeTab: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      chatMessages: [],
      inputMessage: '',
      isLoading: false,
      conversationId: '',
      defaultAvatar: 'https://ai-v2.deepcity.cn/default/head.png',

      // 会话相关
      sessionList: [],
      sessionLoading: false,
      currentSessionGuid: '',
      messagesLoading: false,

      // 智能体详情
      agentDetail: {},

      // 防止重复初始化的标志
      isManualSessionSwitch: false,

      // 防止重复加载消息的标志
      loadingSessionGuid: '',
      conversation_id: '',

      // 小程序端聊天逻辑相关
      isSending: false,
      msgId: '',
      nowAiMsgIndex: 0,
      globalTimer: null,
      showTime: 30, // 打字机效果间隔时间(ms)

      // SSE流式传输状态
      alltext: '',
      isalltext: false,
      isstarted: true,

      // SSE连接引用
      sseSource: null,

      // 购买订单加载状态
      purchaseLoading: false,

      // 订单轮询相关
      currentOrderNo: '',
      pollTimer: null,

      // 防止重复初始化的标志
      isInitializing: false,
      lastInitializedAgent: null,

      // ───────────────────────────────────────────
      // 灰度开关：AI 对话走新版 WebSocket（chatType='agent'）
      // 读取 config.useWsAgentChat，默认 false。
      // 双轨运行至少 2 周，稳定后删除旧 SSE 路径（createSSEConnection / sendOpen）
      // ───────────────────────────────────────────
      useWsAgentChat: !!config.useWsAgentChat,
      // WS 客户端实例（懒加载，仅灰度开关为 true 时创建）
      agentWs: null
    }
  },

  watch: {
    selectedAgent: {
      handler(newAgent, oldAgent) {
        if (newAgent && (!oldAgent || newAgent.guid !== oldAgent.guid)) {
          // 如果是手动会话切换，不要重新初始化
          if (this.isManualSessionSwitch) {
            this.isManualSessionSwitch = false // 重置标志
            return
          }

          // 当切换智能体时，重置聊天消息和当前会话
          this.currentSessionGuid = ''
          this.chatMessages = []

          // 重置初始化标志，允许新的智能体初始化
          this.lastInitializedAgent = null
          this.initAgent()
        }
      },
      immediate: false
    },

    activeTab: {
      handler(newTab) {
        // 当切换到chat tab时，只在没有初始化过的情况下才初始化
        if (newTab === 'chat' && this.selectedAgent && this.lastInitializedAgent !== this.selectedAgent.guid) {
          this.$nextTick(() => {
            this.initAgent()
          })
        }
      }
    }
  },

  mounted() {
    // 如果组件挂载时就是chat tab，且有选中的智能体，立即初始化
    if (this.activeTab === 'chat' && this.selectedAgent) {
      this.$nextTick(() => {
        this.initAgent()
      })
    }
  },

  beforeDestroy() {
    // 清理定时器
    this.stopPolling()
    // 清理 WS 连接（灰度开关开启时才会有实例）
    this.closeAgentWs()
  },

  methods: {
    // 公开的初始化方法，可以被父组件调用
    async initializeChat() {
      await this.initAgent()
    },

    async initAgent() {
      // 防止重复初始化：如果正在初始化或者初始化的是同一个智能体，则跳过
      if (this.isInitializing) {
        return
      }

      const currentAgentGuid = this.selectedAgent?.guid
      if (this.lastInitializedAgent === currentAgentGuid && this.lastInitializedAgent !== null) {
        return
      }

      this.isInitializing = true
      this.lastInitializedAgent = currentAgentGuid

      try {
        // 重置状态（但在手动会话切换时保留currentSessionGuid）
        this.chatMessages = []
        this.conversationId = ''

        // 只有在非手动会话切换时才重置currentSessionGuid
        if (!this.isManualSessionSwitch) {
          this.currentSessionGuid = ''
        }

        this.agentDetail = {}

        // 如果没有选中的智能体，尝试从用户会话列表中获取第一个
        if (!this.selectedAgent) {
          await this.loadUserSessionsAndSelectFirst()
          return
        }

        // 有选中智能体的情况下，正常加载
        await this.loadAgentDetail()

        // 加载该智能体的聊天轮次列表
        await this.loadLatestSession()
      } finally {
        this.isInitializing = false
      }
    },

    async loadUserSessionsAndSelectFirst() {
      // 这个方法不再需要，因为现在是基于智能体的轮次列表
      // 如果没有选中智能体，不做任何操作
      if (!this.selectedAgent) {
        return
      }
    },

    async loadAgentDetail() {
      if (!this.selectedAgent) {
        return
      }

      try {
        const requestData = {
          merchantGuid: this.$store.state.merchantGuid,
          agentGuid: this.selectedAgent.guid || this.selectedAgent.id
        }

        const res = await agentDetailApi(requestData)

        if (res && res.code === 0) {
          this.agentDetail = res.data
        }
      } catch (error) {
        console.error('加载智能体详情失败:', error)
      }
    },

    async loadLatestSession() {
      if (!this.selectedAgent) {
        return
      }

      try {
        // 加载轮次列表
        await this.loadSessionList()

        // 如果有轮次，选择第一个
        if (this.sessionList.length > 0) {
          await this.selectSession(this.sessionList[0])
        }
      } catch (error) {
        console.error('加载最新会话失败:', error)
      }
    },

    async loadSessionList() {
      if (!this.selectedAgent) {
        return
      }

      this.sessionLoading = true
      try {
        const requestData = {
          merchantGuid: this.$store.state.merchantGuid,
          agentGuid: this.selectedAgent.guid || this.selectedAgent.id
        }

        const res = await getSessionListApi(requestData)

        if (res && res.code === 0 && res.data && res.data.list) {
          // 加载当前智能体的聊天轮次列表
          this.sessionList = res.data.list
        }
      } catch (error) {
        console.error('加载聊天轮次列表失败:', error)
      } finally {
        this.sessionLoading = false
      }
    },

    // 新增聊天轮次
    async createNewSession() {
      try {
        this.$prompt('请输入轮次标题', '新增聊天轮次', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputPattern: /\S+/,
          inputErrorMessage: '轮次标题不能为空'
        }).then(async ({ value }) => {
          const requestData = {
            merchantGuid: this.$store.state.merchantGuid,
            agentGuid: this.selectedAgent.guid || this.selectedAgent.id,
            sessionTitle: value
          }

          const res = await createSessionApi(requestData)

          if (res && res.code === 0) {
            this.$message.success('创建成功')
            // 重新加载轮次列表
            await this.loadSessionList()
            // 如果返回了新创建的会话信息，自动选中
            if (res.data && res.data.sessionGuid) {
              this.currentSessionGuid = res.data.sessionGuid
              await this.loadSessionMessages()
            }
          } else {
            this.$message.error(res.msg || '创建失败')
          }
        }).catch(() => {
          // 用户取消
        })
      } catch (error) {
        console.error('创建聊天轮次失败:', error)
        this.$message.error('创建失败')
      }
    },

    async selectSession(session) {
      try {
        // 调用切换轮次接口
        const switchRes = await switchSessionApi({
          merchantGuid: this.$store.state.merchantGuid,
          sessionGuid: session.sessionGuid
        })

        if (switchRes && switchRes.code === 0) {
          // 设置当前会话 GUID
          this.currentSessionGuid = session.sessionGuid

          // 加载该会话的历史消息
          await this.loadSessionMessages()
        } else {
          this.$message.error(switchRes.msg || '切换轮次失败')
        }
      } catch (error) {
        console.error('选择会话失败:', error)
        this.$message.error('选择会话失败')
      }
    },

    async loadSessionMessages() {
      // 防止重复加载同一个会话
      if (this.loadingSessionGuid === this.currentSessionGuid) {
        return
      }

      this.loadingSessionGuid = this.currentSessionGuid
      this.messagesLoading = true
      this.chatMessages = []

      try {
        // 调用 getMessageHistoryApi 获取历史对话记录
        const requestData = {
          merchantGuid: this.$store.state.merchantGuid,
          sessionGuid: this.currentSessionGuid,
          startId: 0, // 默认为0
          page: 1, // 当前页数
          isAll: 0 // 默认为0
        }

        const res = await getMessageHistoryApi(requestData)

        if (res && res.code === 0 && res.data && res.data.list) {
          // 移除 list 数组的最后一条（反转之前的最后一条，即最旧的那条消息）
          const messageList = res.data.list.slice(0, -1) // 使用 slice 移除最后一个元素

          // 将历史消息转换为聊天消息格式
          const historyMessages = messageList.map(msg => {
            return {
              role: msg.chatRole, // 'user' 或 'assistant'
              content: msg.chatContent,
              msgId: msg.msgId,
              guid: msg.guid,
              timestamp: msg.createTime,
              createTimeText: msg.createTimeText,
              isLoading: false,
              starting: false,
              finished: true,
              type: msg.contentType || 'text',
              isCollected: msg.isCollected || false,
              messageType: msg.messageType,
              messageIndex: msg.messageIndex
            }
          })

          // 由于接口返回的是倒序（最新的在前），需要反转数组使其正序显示
          this.chatMessages = historyMessages.reverse()

          // 如果有消息，设置最后一条消息的 msgId
          if (this.chatMessages.length > 0) {
            const lastMsg = this.chatMessages[this.chatMessages.length - 1]
            if (lastMsg.msgId) {
              this.msgId = lastMsg.msgId
            }
          }
        }

        this.scrollToBottom()
      } catch (error) {
        console.error('加载会话消息失败:', error)
        this.$message.error('加载历史消息失败')
      } finally {
        this.messagesLoading = false
        this.loadingSessionGuid = '' // 重置加载标志
      }
    },

    sendPresetQuestion(question) {
      this.inputMessage = question
      this.sendMessage()
    },

    // 复制消息内容
    copyMessage(content) {
      this.$copyText(content)
      this.$message({ message: '已复制', type: 'success' })
    },

    // 收藏消息
    async collectMessage(message) {
      try {
        // 这里可以调用收藏API
        // const res = await collectionChatSave({ msgId: message.msgId })
        this.$message({ message: '收藏成功', type: 'success' })
      } catch (error) {
        this.$message({ message: '收藏失败', type: 'error' })
      }
    },

    // 暂停推理
    onPause() {
      if (this.sseSource) {
        this.sseSource.close()
        this.sseSource = null
      }

      // 停止打字机效果
      if (this.globalTimer) {
        clearInterval(this.globalTimer)
        this.globalTimer = null
      }

      // WS 模式：主动断开连接即可打断流式输出（重连交给下一次 sendMessage）
      if (this.useWsAgentChat) {
        this.closeAgentWs()
      }

      // 更新AI消息状态
      if (this.chatMessages[this.nowAiMsgIndex]) {
        this.chatMessages[this.nowAiMsgIndex].starting = false
        this.chatMessages[this.nowAiMsgIndex].finished = true
        this.chatMessages[this.nowAiMsgIndex].isLoading = false
      }

      this.isSending = false
      this.$message.info('已暂停推理')
    },

    // 添加用户消息
    addUserChat(content) {
      const userMsg = {
        role: 'user',
        content: content,
        msgId: '',
        isLoading: true,
        type: 'text',
        timestamp: new Date(),
        guid: `user_${Date.now()}_${Math.random()}`
      }
      this.chatMessages.push(userMsg) // 添加到末尾（正序显示）
    },

    // 添加AI消息
    addAiChat(answer, type = 'text', isFirst = false) {
      const aiMsg = {
        role: 'assistant',
        content: answer,
        isSuccessData: false,
        isLoading: true,
        starting: true,
        finished: false,
        type: type,
        aiReplyTitle: '',
        isFirst: isFirst,
        isCollected: false,
        msgId: '',
        timestamp: new Date(),
        guid: `ai_${Date.now()}_${Math.random()}`,
        imageList: []
      }
      this.chatMessages.push(aiMsg) // 添加到末尾（正序显示）
    },

    async handleSessionAction(command) {
      const { action, session } = command

      if (action === 'delete') {
        await this.deleteSession(session)
      }
    },

    async deleteSession(session) {
      try {
        await this.$confirm('确定要删除这个聊天轮次吗？删除后将无法恢复。', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        const res = await deleteSessionByIdApi({
          merchantGuid: this.$store.state.merchantGuid,
          sessionGuid: session.sessionGuid
        })

        if (res.code === 0) {
          this.$message.success('轮次已删除')

          // 如果删除的是当前会话，清空聊天记录并选择第一个轮次
          if (this.currentSessionGuid === session.sessionGuid) {
            this.chatMessages = []
            this.currentSessionGuid = ''
          }

          // 重新加载轮次列表
          await this.loadSessionList()

          // 如果还有其他轮次，自动选择第一个
          if (this.sessionList.length > 0 && !this.currentSessionGuid) {
            await this.selectSession(this.sessionList[0])
          }
        } else {
          this.$message.error(res.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除轮次失败:', error)
          this.$message.error('删除失败')
        }
      }
    },

    // 静默创建聊天轮次：发送消息时若无 currentSessionGuid 自动建一条
    // 标题取首段用户内容前 20 字，便于在侧栏列表中识别
    async ensureSession(firstContent) {
      if (!this.selectedAgent) {
        this.$message.error('请先选择智能体')
        return false
      }
      const title = (firstContent || '新对话').trim().slice(0, 20) || '新对话'
      try {
        const res = await createSessionApi({
          merchantGuid: this.$store.state.merchantGuid,
          agentGuid: this.selectedAgent.guid || this.selectedAgent.id,
          sessionTitle: title
        })
        if (res && res.code === 0 && res.data && res.data.sessionGuid) {
          this.currentSessionGuid = res.data.sessionGuid
          // 后台刷新侧栏，不阻塞发送
          this.loadSessionList()
          return true
        }
        this.$message.error((res && res.msg) || '创建会话失败')
        return false
      } catch (e) {
        console.error('自动创建会话失败:', e)
        this.$message.error('创建会话失败')
        return false
      }
    },

    async sendMessage() {
      // 检查是否有内容
      if (!this.inputMessage.trim()) {
        this.$message.warning('请输入问题...')
        return
      }

      if (this.isSending) {
        this.$message.warning('回答中...')
        return
      }

      this.isSending = true

      const userContent = this.inputMessage.trim()

      // 若当前没有活跃聊天轮次，先静默创建一条（对齐小程序：发送即建轮次）
      // Node WS 的 start 接口会强校验 sessionGuid 非空，否则 [40410]: sessionGuid 不能为空
      if (!this.currentSessionGuid) {
        const ok = await this.ensureSession(userContent)
        if (!ok) {
          this.isSending = false
          return
        }
      }

      // 清除之前的定时器，防止多个定时器同时运行
      if (this.globalTimer) {
        clearInterval(this.globalTimer)
        this.globalTimer = null
      }

      // 添加用户消息
      this.addUserChat(userContent)

      // 添加AI回复占位符
      this.addAiChat(' ')
      this.nowAiMsgIndex = this.chatMessages.length - 1 // AI消息在最后面（正序显示）

      // 清空输入框
      this.inputMessage = ''

      // ───────────────────────────────────────────
      // 灰度分流：useWsAgentChat === true → 走 Node WebSocket
      // 否则走旧 saveMsg + sendOpen(SSE) 双接口路径
      // ───────────────────────────────────────────
      if (this.useWsAgentChat) {
        this.sendMessageViaWs(userContent)
        return
      }

      // 准备用户消息数据（旧 SSE 路径，保留以便灰度回滚）
      const msgUserReqParame = {
        merchantGuid: this.$store.state.merchantGuid,
        sessionGuid: this.currentSessionGuid,
        role: 'user',
        content: userContent,
        lastMsgId: this.msgId,
        contentType: 'text',
        sceneValue: '',
        chatLunciGuid: ''
      }

      try {
        // 保存用户消息
        const msgReqRes = await saveMsgApi(msgUserReqParame)
        if (msgReqRes.code !== 0) {
          if (msgReqRes.code === 900001) {
            // 试用次数已用完，显示错误消息和购买按钮
            this.chatMessages[this.nowAiMsgIndex].content = msgReqRes.msg || '试用次数已用完，请购买激活'
            this.chatMessages[this.nowAiMsgIndex].type = 'purchase_needed' // 添加类型标识
            this.chatMessages[this.nowAiMsgIndex].starting = false // 停止思考中状态
            this.chatMessages[this.nowAiMsgIndex].isLoading = false // 停止加载状态
          } else {
            this.chatMessages[this.nowAiMsgIndex].content = '服务繁忙，请稍候重试';
            this.chatMessages[this.nowAiMsgIndex].starting = false // 停止思考中状态
            this.chatMessages[this.nowAiMsgIndex].isLoading = false // 停止加载状态
          }
          this.isSending = false;
          return
        }


        this.msgId = msgReqRes.data.msgId
        this.chatMessages[this.nowAiMsgIndex - 1].msgId = msgReqRes.data.msgId

        // 开始SSE连接
        this.createSSEConnection()

      } catch (error) {
        console.error('发送消息失败:', error)
        this.isSending = false
        this.chatMessages[this.nowAiMsgIndex].isLoading = false

        this.chatMessages[this.nowAiMsgIndex].content = `${error.msg || '发送失败'}-${error.code || ''}`
      }
    },

    // ──────────────────────────────────────────────────────────────
    // 新版 WebSocket 路径（灰度开关 useWsAgentChat 开启时走这里）
    // 事件映射：
    //   text_chunk      → 累加到当前 assistant 消息 content（打字机效果由此刷新）
    //   tool_start      → 占位提示「执行 ${toolName}...」
    //   tool_done       → 清除占位
    //   structured_data → 写入 message.structuredData（UI 如有商品卡片组件渲染此字段）
    //   done            → 结束打字机/loading，保留 msgId 作下一轮 lastMsgId
    //   error           → 展示错误信息（兼容 900001 购买提示）
    // ──────────────────────────────────────────────────────────────
    ensureAgentWs() {
      if (!this.agentWs) {
        this.agentWs = new AgentChatWs({ url: config.wsUrl })
        this.bindAgentWsEvents(this.agentWs)
      }
      if (!this.agentWs.isOpen()) {
        this.agentWs.connect()
      }
      return this.agentWs
    },

    bindAgentWsEvents(ws) {
      ws.on('text_chunk', (ev) => {
        const msg = this.chatMessages[this.nowAiMsgIndex]
        if (!msg) return
        // 首次有文本输出时，清除占位空格 & 关闭「思考中」
        if (msg.starting) {
          msg.content = ''
          msg.starting = false
        }
        msg.content = (msg.content || '') + (ev.content || '')
        msg.isLoading = true
      })
      ws.on('tool_start', (ev) => {
        const msg = this.chatMessages[this.nowAiMsgIndex]
        if (!msg) return
        msg.starting = false
        msg.toolLabel = ev.label || `执行 ${ev.toolName || '工具'}...`
        msg.isTooling = true
      })
      ws.on('tool_done', () => {
        const msg = this.chatMessages[this.nowAiMsgIndex]
        if (!msg) return
        msg.isTooling = false
        msg.toolLabel = ''
      })
      ws.on('structured_data', (ev) => {
        const msg = this.chatMessages[this.nowAiMsgIndex]
        if (!msg) return
        this.$set(msg, 'structuredData', ev.data)
      })
      ws.on('done', (ev) => {
        const msg = this.chatMessages[this.nowAiMsgIndex]
        if (msg) {
          msg.starting = false
          msg.finished = true
          msg.isLoading = false
          msg.isTooling = false
          if (ev.msgId) msg.msgId = ev.msgId
        }
        // 更新 lastMsgId 供下一轮使用
        if (ev.msgId) this.msgId = ev.msgId
        this.isSending = false
        this.$nextTick(() => this.scrollToBottom())
      })
      ws.on('error', (ev) => {
        const msg = this.chatMessages[this.nowAiMsgIndex]
        if (msg) {
          msg.starting = false
          msg.isLoading = false
          msg.isTooling = false
          msg.finished = true
          // 服务端返回的试用次数已满错误（沿用旧接口约定）
          if (ev && ev.code === 900001) {
            msg.content = ev.message || '试用次数已用完，请购买激活'
            msg.type = 'purchase_needed'
          } else {
            msg.content = (ev && ev.message) || '连接失败，请重试'
          }
        }
        this.isSending = false
        if (ev && ev.message && (!ev.code || ev.code !== 900001)) {
          this.$message.error(ev.message)
        }
      })
      ws.on('close', () => {
        // 连接断开时若仍在发送中，交由 WS 封装自动重连；UI 不做强复位
      })
    },

    sendMessageViaWs(userContent) {
      const ws = this.ensureAgentWs()
      const token = getToken()
      const merchantGuid = this.$store.state.merchantGuid || ''
      const agentGuid = (this.selectedAgent && (this.selectedAgent.guid || this.selectedAgent.id)) || ''

      const payload = {
        sessionGuid: this.currentSessionGuid,
        content: userContent,
        lastMsgId: this.msgId || '',
        imgs: [],
        videos: [],
        fileUrls: [],
        token,
        app_type: 'xiaoyipc',
        app_guid: merchantGuid,
        // 部分服务端可能按 sceneValue 取 agentGuid，双保险透传
        sceneValue: agentGuid,
      }

      // 若 WS 尚未建立完成，等待 open 事件后再发
      if (ws.isOpen()) {
        ws.sendMessage(payload)
      } else {
        const onceOpen = () => {
          ws.off('open', onceOpen)
          ws.sendMessage(payload)
        }
        ws.on('open', onceOpen)
      }
    },

    closeAgentWs() {
      if (this.agentWs) {
        try { this.agentWs.close() } catch (_) { /* ignore */ }
        this.agentWs = null
      }
    },

    // 创建SSE连接（按照小程序端逻辑）
    createSSEConnection() {
      // 使用组件数据来存储状态
      this.alltext = ''
      this.isalltext = false
      this.isstarted = true

      // 清除之前的定时器，防止多个定时器同时运行
      if (this.globalTimer) {
        clearInterval(this.globalTimer)
        this.globalTimer = null
      }

      // 构建SSE URL
      // https://xyapi.cloneyou.cn//useragent/api.AiAgentChat/sendOpen?msgId=79f6fb3180f0d8ab3473bddf03eacded&chatLunciGuid=a1d840da12e6446492d427f5438c133e&merchantGuid=938d1d20d13c4130a79f3d7590e62720&conversation_id=
      const url = `${config.baseUrl}/useragent/api.AiAgentChat/sendOpen?msgId=${this.msgId}&chatLunciGuid=${this.currentSessionGuid}&merchantGuid=${this.$store.state.merchantGuid}&conversation_id=${this.conversation_id}`
      this.sseSource = new EventSource(url)
      const source = this.sseSource
      source.onmessage = (event) => {
        try {
          if (this.isstarted) {
            this.isstarted = false
            // 开始打字机效果
            this.startTypingEffect()
          }
          const data = event.data

          if (data === '[DONE]') {
            // 从最后一个数据中提取conversation_id
            // conversation_id.value = textArr[textArr.length - 2].id;
            this.isalltext = true
            source.close()
            this.sseSource = null
            return
          }

          if (data) {
            if (this.alltext === '') {
              this.alltext = data.replace(/^\n+/, '')
            } else {
              this.alltext += data
            }
          }

        } catch (error) {
          this.handleSSEError('处理数据失败')
          source.close()
          this.sseSource = null
        }
      }

      source.onerror = (error) => {
        let data = JSON.parse(error.data)
        console.error('SSE连接错误:', error.data, data.code, data.msg)
        if (data.code == 4101) {
          this.handleSSEError(data.msg)
        } else {
          this.handleSSEError('连接失败，请重试')
        }
        this.onPause()
      }

      // 设置超时
      // setTimeout(() => {
      //   if (source.readyState !== EventSource.CLOSED) {
      //     source.close()
      //     this.sseSource = null
      //     this.handleSSEError('响应超时，请重试')
      //   }
      // }, 60000) // 60秒超时
    },

    // 开始打字机效果（按照小程序端逻辑）
    startTypingEffect() {
      let temp = '' // 临时显示文本
      let index = 0 // 初始下标为0

      this.globalTimer = setInterval(async () => {
        this.alltext = this.alltext.replace(/\\n/g, '\n')
        const previousTemp = temp

        if (temp.length < this.alltext.length) {
          // 如果比原始文本短，就往临时显示文本添加一个字符
          temp += this.alltext[index]
          index = index + 1
        } else {
          if (this.isalltext) {
            // 清除定时器
            this.isSending = false
            clearInterval(this.globalTimer)
            this.globalTimer = null

            // 保存AI消息
            await this.saveAiMessage(temp)

            this.chatMessages[this.nowAiMsgIndex].isLoading = false
            this.chatMessages[this.nowAiMsgIndex].starting = false
            this.chatMessages[this.nowAiMsgIndex].finished = true

            this.$nextTick(() => {
              this.scrollToBottom()
            })
          }
        }

        // 只有当内容发生变化时才更新显示文本，减少不必要的DOM更新
        if (temp !== previousTemp) {
          this.chatMessages[this.nowAiMsgIndex].content = temp
          this.chatMessages[this.nowAiMsgIndex].starting = false
        }
      }, this.showTime)
    },

    // 保存AI消息
    async saveAiMessage(content) {
      try {
        const msgAiReqParame = {
          merchantGuid: this.$store.state.merchantGuid,
          role: 'assistant',
          sessionGuid: this.currentSessionGuid,
          content: content,
          lastMsgId: this.msgId,
          contentType: 'richText',
          sceneValue: '',
          chatLunciGuid: ''
        }

        const msgReqRes = await saveMsgApi(msgAiReqParame)
        if (msgReqRes.code === 0) {
          this.msgId = msgReqRes.data.msgId
          this.chatMessages[this.nowAiMsgIndex].guid = msgReqRes.data.messageGuid
          this.chatMessages[this.nowAiMsgIndex].isSuccessData = true
        } else {
          if (msgReqRes.code === 900001) {
            // 试用结束，显示错误消息和购买按钮
            this.chatMessages[this.nowAiMsgIndex].content = msgReqRes.msg || '试用次数已用完，请购买激活'
            this.chatMessages[this.nowAiMsgIndex].type = 'purchase_needed' // 添加类型标识
            this.chatMessages[this.nowAiMsgIndex].starting = false // 停止思考中状态
            this.chatMessages[this.nowAiMsgIndex].isLoading = false // 停止加载状态
          } else {
            this.chatMessages[this.nowAiMsgIndex].content = '保存消息失败'
            this.chatMessages[this.nowAiMsgIndex].starting = false // 停止思考中状态
            this.chatMessages[this.nowAiMsgIndex].isLoading = false // 停止加载状态
          }
        }
      } catch (error) {
        console.error('保存AI消息失败:', error)
        this.chatMessages[this.nowAiMsgIndex].content = '保存消息失败'
      }
    },

    // 处理SSE错误
    handleSSEError(message) {
      this.isSending = false
      this.chatMessages[this.nowAiMsgIndex].isLoading = false
      this.chatMessages[this.nowAiMsgIndex].starting = false
      this.chatMessages[this.nowAiMsgIndex].content = message

      // 清除定时器
      if (this.globalTimer) {
        clearInterval(this.globalTimer)
        this.globalTimer = null
      }
    },

    handleError(message) {
      const lastMessage = this.chatMessages[this.chatMessages.length - 1]
      if (lastMessage && lastMessage.role === 'assistant') {
        lastMessage.starting = false
        lastMessage.content = message
        lastMessage.error = true
      }
      this.isLoading = false
      this.$message.error(message)
    },

    async clearChat() {
      if (!this.currentSessionGuid) {
        this.chatMessages = []
        this.$message.success('对话已清空')
        return
      }

      try {
        await this.$confirm('确定要清空当前对话吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        const res = await deleteAllMessagesApi({
          merchantGuid: this.$store.state.merchantGuid,
          sessionGuid: this.currentSessionGuid
        })

        if (res.code === 0) {
          await this.loadSessionMessages()
          this.$message.success('对话已清空')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('清空对话失败:', error)
          this.$message.error('清空失败')
        }
      }
    },


    goToMarketplace() {
      this.$emit('go-to-marketplace')
    },

    formatMessage(content) {
      if (!content) return ''
      return content.replace(/\n/g, '<br>')
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const chatHistory = this.$refs.chatHistory
        if (chatHistory) {
          chatHistory.scrollTop = chatHistory.scrollHeight
        }
      })
    },



    // 处理购买订单
    async handlePurchaseOrder() {
      if (this.purchaseLoading) return

      try {
        this.purchaseLoading = true
        const params = {
          merchantGuid: this.$store.state.merchantGuid,
          agentGuid: this.selectedAgent.guid,
          payEnv: 'alipay_pc'
        }

        console.log('创建购买订单参数:', params)
        const response = await createPurchaseOrderApi(params)
        console.log('创建购买订单响应:', response)

        if (response.code === 0) {
          // 订单创建成功，处理支付流程
          const { orderNo, payInfo } = response.data
          this.currentOrderNo = orderNo

          // 解析并提交支付表单
          if (payInfo && payInfo.payForm) {
            this.submitPayForm(payInfo.payForm)
            // 开始轮询订单状态
            this.startPollingOrderStatus()
          } else {
            this.$message.error('支付信息获取失败')
          }
        } else {
          this.$message.error(response.msg || '订单创建失败')
        }
      } catch (error) {
        console.error('创建购买订单失败:', error)
        this.$message.error('订单创建失败，请稍后重试')
      } finally {
        this.purchaseLoading = false
      }
    },

    // 提交支付表单到支付宝
    submitPayForm(payForm) {
      try {
        // 解码base64编码的支付表单
        const decodedForm = window.atob(payForm)

        // 创建临时div来解析HTML
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = decodedForm

        // 获取表单元素
        const form = tempDiv.querySelector('form')
        if (form) {
          // 设置表单目标为新窗口
          form.target = '_blank'

          // 将表单添加到页面并提交
          document.body.appendChild(form)
          form.submit()

          // 提交后移除表单
          document.body.removeChild(form)

          this.$message.info('正在跳转到支付页面...')
        } else {
          this.$message.error('支付表单解析失败')
        }
      } catch (error) {
        console.error('提交支付表单失败:', error)
        this.$message.error('支付页面跳转失败')
      }
    },

    // 开始轮询订单状态
    startPollingOrderStatus() {
      let pollCount = 0
      const maxPolls = 3 // 最多轮询3次

      this.pollTimer = setInterval(async () => {
        pollCount++
        console.log(`第${pollCount}次查询订单状态, 订单号:`, this.currentOrderNo)

        try {
          const response = await queryPurchaseOrderApi({
            orderNo: this.currentOrderNo
          })

          console.log('订单状态查询响应:', response)

          if (response.code === 0) {
            const { isPaid } = response.data

            if (isPaid) {
              // 支付成功
              this.handlePaymentSuccess()
              return
            }
          }

          // 达到最大轮询次数，停止轮询
          if (pollCount >= maxPolls) {
            this.stopPolling()
            console.log('订单状态轮询结束，未检测到支付成功')
          }
        } catch (error) {
          console.error('查询订单状态失败:', error)
          if (pollCount >= maxPolls) {
            this.stopPolling()
          }
        }
      }, 3000) // 每3秒查询一次
    },

    // 停止轮询
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },

    // 处理支付成功
    handlePaymentSuccess() {
      this.stopPolling()
      this.$message.success('支付成功！您可以继续聊天了')

      // 清除当前订单号
      this.currentOrderNo = ''

      // 这里可以添加其他支付成功后的处理逻辑
      // 比如刷新用户信息、重新发送消息等
    },

    // 显示购买套餐弹窗
    showPayMeal() {
      this.$refs.paymeal.show()
    },

    // 显示购买会员弹窗
    showVipDialog() {
      this.$refs.vipDialogRef.show()
    },

    // 购买套餐成功回调
    onBuyMeal() {
      this.$message.success('购买成功！')
      // 可以在这里刷新用户信息或重新发送消息
    },

    // 购买会员成功回调
    onBuyVip() {
      this.$message.success('购买成功！')
      // 可以在这里刷新用户信息或重新发送消息
    }
  }
}
</script>

<style lang="scss" scoped>
.agent-chat {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;

  .agent-info-bar {
    background: white;
    padding: 12px 16px;
    border-bottom: 1px solid #e8e8e8;
    display: flex;
    align-items: center;
    gap: 12px;

    .agent-avatar {
      img {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        object-fit: cover;
      }
    }

    .agent-details {
      flex: 1;

      .agent-name {
        margin: 0 0 2px 0;
        font-size: 15px;
        font-weight: 600;
        color: #333;
        line-height: 1.3;
      }

      .agent-description {
        margin: 0;
        font-size: 12px;
        color: #666;
        line-height: 1.3;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }

    .agent-actions {
      display: flex;
      gap: 6px;

      .el-button {
        height: 28px;
        padding: 0 10px;
        font-size: 12px;
      }
    }
  }

  .chat-container {
    flex: 1;
    display: flex;
    min-height: 0; // 允许flex子元素收缩

    .chat-list {
      width: 260px;
      background: white;
      border-right: 1px solid #e8e8e8;
      display: flex;
      flex-direction: column;

      .session-header {
        padding: 12px 16px;
        border-bottom: 1px solid #e8e8e8;
        display: flex;
        justify-content: space-between;
        align-items: center;

        h4 {
          margin: 0;
          font-size: 16px;
          color: #333;
          font-weight: 500;
        }

        .header-actions {
          display: flex;
          gap: 4px;

          .el-button {
            padding: 4px;
            min-height: auto;
          }
        }
      }

      .session-list {
        flex: 1;
        overflow-y: auto;

        .session-item {
          padding: 8px 16px;
          border-bottom: 1px solid #f0f0f0;
          cursor: pointer;
          transition: background-color 0.2s;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;

          &:hover {
            background: #f8f9fa;
          }

          &.active {
            background: #e8f4fd;
            border-left: 2px solid #7848f1;
          }

          .session-info {
            flex: 1;
            min-width: 0;

            .session-title {
              font-size: 15px;
              font-weight: 500;
              color: #333;
              margin-bottom: 4px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            .session-meta {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 12px;
              color: #999;

              .session-time {
                flex-shrink: 0;
              }

              .session-count {
                flex-shrink: 0;
                padding: 2px 6px;
                background: #f0f0f0;
                border-radius: 10px;
                font-size: 11px;
              }
            }
          }

          .session-actions {
            margin-left: 6px;

            .el-dropdown-link {
              color: #999;
              cursor: pointer;
              padding: 2px;
              font-size: 12px;

              &:hover {
                color: #7848f1;
              }
            }
          }
        }
      }
    }

    .chat-content {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
  }

  .no-agent-selected {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    .empty-state {
      text-align: center;

      i {
        font-size: 64px;
        color: #ddd;
        margin-bottom: 16px;
      }

      h3 {
        margin: 0 0 8px 0;
        color: #333;
      }

      p {
        margin: 0 0 24px 0;
        color: #666;
      }
    }
  }

  .chat-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0; // 允许flex子元素收缩

    .chat-history {
      flex: 1;
      padding: 16px;
      overflow-y: auto;
      min-height: 200px; // 确保聊天历史区域有最小高度

      // 智能体介绍消息的特殊样式
      .agent-intro-message {
        .content {
          background: linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%) !important;
          border: 1px solid #e1e8f0;
          border-radius: 8px;
          padding: 12px;

          .agent-detail-inline {
            h4 {
              margin: 0 0 6px 0;
              font-size: 16px;
              font-weight: 600;
              color: #333;
            }

            .agent-desc-inline {
              margin: 0 0 12px 0;
              font-size: 14px;
              color: #666;
              line-height: 1.4;
            }

            .common-questions-inline {
              .questions-title {
                font-size: 12px;
                font-weight: 500;
                color: #7848f1;
                margin-bottom: 8px;
              }

              .question-list-inline {
                display: flex;
                flex-wrap: wrap;
                gap: 6px;

                .question-item-inline {
                  display: inline-block;
                  padding: 4px 8px;
                  background: #fff;
                  border: 1px solid #d1d5db;
                  border-radius: 12px;
                  font-size: 12px;
                  color: #374151;
                  cursor: pointer;
                  transition: all 0.2s ease;

                  &:hover {
                    background: #7848f1;
                    color: white;
                    border-color: #7848f1;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 4px rgba(120, 72, 241, 0.2);
                  }
                }
              }
            }
          }
        }
      }

      .message {
        display: flex;
        gap: 12px;

        &.user {
          flex-direction: row-reverse;

          .content {
            text-align: right;

            .message-content {
              background: #7848f1;
              color: white;
            }
          }
        }

        .avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f0f0f0;
          flex-shrink: 0;

          img {
            width: 100%;
            height: 100%;
            border-radius: 50%;
            object-fit: cover;
          }

          i {
            font-size: 16px;
            color: #666;
          }
        }

        .content {
          flex: 1;
          max-width: 70%;

          .message-content {
            background: white;
            padding: 8px 12px;
            border-radius: 8px;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
            word-wrap: break-word;
            line-height: 1.4;
            font-size: 13px;

            .typing-indicator {
              display: flex;
              gap: 3px;

              span {
                width: 6px;
                height: 6px;
                border-radius: 50%;
                background: #999;
                animation: typing 1.4s infinite ease-in-out;

                &:nth-child(1) {
                  animation-delay: -0.32s;
                }

                &:nth-child(2) {
                  animation-delay: -0.16s;
                }
              }
            }
          }

          .message-time {
            font-size: 11px;
            color: #999;
            margin-top: 2px;
          }
        }
      }

      // 按照chat-box样式重写的聊天项
      .chat-item {
        display: flex;
        padding: 12px 16px;
        box-sizing: border-box;

        &.mine {
          flex-direction: row-reverse;

          .content {
            background-color: #f6f7f9;
            color: #120649;
            padding: 8px 10px;
          }
        }

        .header-img {
          img {
            width: 32px;
            height: 32px;
            border-radius: 100%;
          }

          .user-avatar-icon {
            width: 32px;
            height: 32px;
            border-radius: 100%;
            background: #7848f1;
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
          }
        }

        .copy {
          font-size: 12px;
          display: flex;
          align-items: center;
          flex-direction: column;

          a {
            cursor: pointer;
            color: #666;
            transition: color 0.2s;

            &:hover {
              color: #7848f1;
            }
          }

          .star {
            margin-top: 10px;
            font-size: 14px;
            text-align: center;
          }
        }

        .content {
          border-radius: 6px;
          max-width: 70%;
          min-width: 80px;
          padding: 0px 8px;
          margin-left: 8px;
          font-size: 16px;
          word-wrap: break-word;
          overflow-wrap: break-word;
          white-space: normal;
          text-overflow: ellipsis;
          position: relative;

          .loading {
            padding: 8px;
            color: #666;
            font-style: italic;
            font-size: 11px;
          }

          .pause-box {
            display: flex;
            align-items: center;
            cursor: pointer;
            color: #4958ec;
            font-size: 12px;
            position: absolute;
            bottom: -22px;
            left: 8px;

            .el-icon-video-pause {
              font-size: 14px;
              margin-right: 4px;
            }
          }
        }
      }
    }
  }

  .chat-input {
    background: white;
    // border-top: 1px solid #e8e8e8;
    padding: 8px 6px;
    flex-shrink: 0; // 防止输入区域被压缩

    .input-container {
      .input-row {
        display: flex;
        gap: 8px;
        align-items: flex-end;

        .message-input {
          flex: 1;

          ::v-deep .el-textarea__inner {
            font-size: 13px;
            line-height: 1.4;
            padding: 8px 10px;
            border-radius: 6px;
            resize: none; // 禁止手动调整大小
          }
        }

        .send-button {
          height: 40px; // 匹配textarea的高度
          padding: 0 16px;
          font-size: 13px;
          flex-shrink: 0;
        }
      }

      .input-tips {
        display: flex;

        .shortcut-tip {
          font-size: 11px;
          color: #999;
        }
      }
    }
  }
}

// 添加chat-box中的preview-box样式
.preview-box .github-markdown-body {
  padding: 0px;
  background-color: inherit;
  font-size: inherit;
  color: inherit;
  letter-spacing: 1px;
}

.preview-box .github-markdown-body p {
  margin-bottom: 0px;
}

@keyframes typing {

  0%,
  80%,
  100% {
    transform: scale(0);
  }

  40% {
    transform: scale(1);
  }
}

// 购买按钮样式
.purchase-buttons {
  margin-top: 10px;
  display: flex;
  gap: 10px;

  .el-button {
    font-size: 13px;

    &:hover {
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }
}
</style>
